'use client';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

export function trackPageView(url: string, title?: string): void {
  if (typeof window === 'undefined' || !window.gtag || !GA_MEASUREMENT_ID) return;
  window.gtag('event', 'page_view', {
    page_path: url,
    page_title: title,
    send_to: GA_MEASUREMENT_ID,
  });
}

export function trackEvent(
  eventName: string,
  eventParams: Record<string, string | number | boolean> = {}
): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', eventName, eventParams);
}

export function trackFormSubmission(params: {
  pageSource: string;
  enquiryType: string;
  urgency: string;
  hasPhone: boolean;
}): void {
  trackEvent('compliance_form_submit', {
    event_category: 'Enquiry',
    event_label: params.enquiryType,
    page_source: params.pageSource,
    enquiry_type: params.enquiryType,
    urgency_level: params.urgency,
    has_phone: params.hasPhone,
  });

  // Also fire as a GA4 conversion event
  trackEvent('generate_lead', {
    event_category: 'Lead',
    page_source: params.pageSource,
    enquiry_type: params.enquiryType,
    urgency_level: params.urgency,
  });
}

export function trackFormStart(pageSource: string): void {
  trackEvent('compliance_form_start', {
    event_category: 'Enquiry',
    page_source: pageSource,
  });
}

export function trackFormError(pageSource: string, errorField: string): void {
  trackEvent('compliance_form_error', {
    event_category: 'Enquiry',
    page_source: pageSource,
    error_field: errorField,
  });
}

export function trackEnquiryTypeChange(enquiryType: string, pageSource: string): void {
  trackEvent('enquiry_type_selected', {
    event_category: 'Enquiry',
    enquiry_type: enquiryType,
    page_source: pageSource,
  });
}

export function trackUrgencySelected(urgency: string, pageSource: string): void {
  trackEvent('urgency_selected', {
    event_category: 'Enquiry',
    urgency_level: urgency,
    page_source: pageSource,
  });
}

// ── Properties / Listings tracking ──────────────────────────────────────────

export function trackPropertySearch(params: {
  bedrooms?: string;
  minPrice?: string;
  maxPrice?: string;
  area?: string;
  resultCount: number;
}): void {
  trackEvent('property_search', {
    event_category: 'Properties',
    bedrooms_filter: params.bedrooms || 'any',
    min_price_filter: params.minPrice || 'any',
    max_price_filter: params.maxPrice || 'any',
    area_filter: params.area || 'any',
    result_count: params.resultCount,
  });
}

export function trackPropertyFilterChange(filterName: string, filterValue: string): void {
  trackEvent('property_filter_change', {
    event_category: 'Properties',
    filter_name: filterName,
    filter_value: filterValue,
  });
}

export function trackPropertyView(propertyId: string, propertyTitle: string): void {
  trackEvent('property_view', {
    event_category: 'Properties',
    property_id: propertyId,
    property_title: propertyTitle,
  });
}

// ── Contact Page tracking ────────────────────────────────────────────────────

export function trackContactFormSubmit(params: {
  enquiryType: string;
  hasPhone: boolean;
}): void {
  trackEvent('contact_form_submit', {
    event_category: 'Contact',
    event_label: params.enquiryType,
    enquiry_type: params.enquiryType,
    has_phone: params.hasPhone,
    page_source: 'contact',
  });

  trackEvent('generate_lead', {
    event_category: 'Lead',
    page_source: 'contact',
    enquiry_type: params.enquiryType,
  });
}

export function trackContactFormStart(): void {
  trackEvent('contact_form_start', {
    event_category: 'Contact',
    page_source: 'contact',
  });
}

// ── Blog tracking ────────────────────────────────────────────────────────────

export function trackBlogArticleClick(params: {
  slug: string;
  title: string;
  category: string;
}): void {
  trackEvent('blog_article_click', {
    event_category: 'Blog',
    article_slug: params.slug,
    article_title: params.title,
    article_category: params.category,
  });
}

export function trackBlogSearch(searchTerm: string, resultCount: number): void {
  trackEvent('blog_search', {
    event_category: 'Blog',
    search_term: searchTerm,
    result_count: resultCount,
  });
}

export function trackBlogCategoryFilter(category: string): void {
  trackEvent('blog_category_filter', {
    event_category: 'Blog',
    category,
  });
}
